import { CartUpdatedEvent } from '@theme/events';

/**
 * Cadeau Coalition (remplace EasyGift).
 *
 * Le snippet coalition-cadeau décide de tout côté serveur ; ce script exécute :
 * - clic sur un parfum : ajoute le cadeau (propriété `_cadeau`), en remplaçant l'ancien s'il y en a un ;
 * - `data-updates` : retire les lignes cadeau non méritées ou les ramène à 1, dès l'affichage.
 * Puis le panier latéral est redessiné (la page /cart, elle, est rechargée, comme le reste du thème).
 */
const LOOP_KEY = 'coalition-cadeau-nettoyage';

const json = (body) => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body: JSON.stringify(body),
});

async function post(url, body) {
  const response = await fetch(url, json(body));
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erreur panier');
  return data;
}

const drawerSectionId = () => document.querySelector('cart-drawer')?.dataset.sectionId;
const onCartPage = () => window.location.pathname === Theme.routes.cart_url;

/** Redessine le panier latéral (ou recharge la page panier) après une modification. */
async function refresh(sections) {
  if (onCartPage()) {
    window.location.reload();
    return;
  }
  const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
  document.dispatchEvent(
    new CartUpdatedEvent({}, { itemCount: cart.item_count, source: 'coalition-cadeau', sections }),
  );
}

/** Évite une boucle si le même nettoyage revient sans cesse (au plus 2 essais en 10 s). */
function mayClean(signature) {
  try {
    const last = JSON.parse(sessionStorage.getItem(LOOP_KEY) || 'null');
    const tries = last?.signature === signature && Date.now() - last.time < 10000 ? last.tries + 1 : 1;
    sessionStorage.setItem(LOOP_KEY, JSON.stringify({ signature, time: Date.now(), tries }));
    return tries <= 2;
  } catch {
    return true;
  }
}

let cleaning = null;

class CoalitionCadeau extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-variant-id]').forEach((button) =>
      button.addEventListener('click', () => this.#choose(button)),
    );
    if (this.dataset.updates) this.#clean(this.dataset.updates);
  }

  async #choose(button) {
    this.#busy(true);
    try {
      const sections = drawerSectionId();
      if (this.dataset.garde) {
        await post(`${Theme.routes.cart_update_url}.js`, { updates: { [this.dataset.garde]: 0 } });
      }
      const added = await post(Theme.routes.cart_add_url, {
        items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _cadeau: this.dataset.cle } }],
        ...(sections && { sections, sections_url: window.location.pathname }),
      });
      await refresh(added.sections);
    } catch (error) {
      this.#busy(false);
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
    }
  }

  async #clean(updates) {
    // Le cadeau est rendu à la fois dans le panier latéral et sur la page panier : un seul envoi.
    if (cleaning === updates || !mayClean(updates)) return;
    cleaning = updates;
    try {
      const sections = drawerSectionId();
      const cart = await post(`${Theme.routes.cart_update_url}.js`, {
        updates: JSON.parse(updates),
        ...(sections && { sections, sections_url: window.location.pathname }),
      });
      await refresh(cart.sections);
    } catch (error) {
      console.error('Cadeau Coalition :', error);
    } finally {
      cleaning = null;
    }
  }

  #busy(on) {
    this.toggleAttribute('aria-busy', on);
    this.querySelectorAll('button').forEach((button) => (button.disabled = on));
  }
}

if (!customElements.get('coalition-cadeau')) {
  customElements.define('coalition-cadeau', CoalitionCadeau);
}
